import * as Sentry from '@sentry/react';
import './index.css';
import { ROUTES } from './lib/routes';
import { SystemLayout } from './routes/app/app-layout';
import AuthLayout from './routes/auth-layout';
import { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router';
import GlobalErrorBoundary from './routes/error-boundary';

// Lazy load pages
const LoginPage = lazy(() => import('./routes/login/login'));
const HomePage = lazy(() => import('./routes/app/home'));
const ProfilePage = lazy(() => import('./routes/app/profile/profile-page'));
const SystemSettingsPage = lazy(
  () => import('./routes/app/settings/system-settings-page'),
);
const CreateEmployeePage = lazy(() => import('./routes/app/employees/create-employee'));
const EmployeeListPage = lazy(() => import('./routes/app/employees/employee-list'));
const PayrollManagerPage = lazy(() => import('./routes/app/payroll/payroll-manager'));
const PayrollDetailPage = lazy(() => import('./routes/app/payroll/payroll-detail'));
const EmployeeDetailPage = lazy(() => import('./routes/app/employees/employee-detail'));
const SettlementCalculatorPage = lazy(() => import('./routes/app/employees/settlement-calculator'));
const LegalReportsPage = lazy(() => import('./routes/app/reports/legal-reports-page'));

// Loading fallback
function LoadingFallback() {
  return (
    <div className="bg-background flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  // Auth routes
  {
    errorElement: <GlobalErrorBoundary />,
    element: <AuthLayout />,
    children: [
      {
        path: ROUTES.PAGE.LOGIN,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: '/app',
        element: (
          <Sentry.ErrorBoundary
            fallback={(errorProps) => <GlobalErrorBoundary {...errorProps} />}
          >
            <SystemLayout />
          </Sentry.ErrorBoundary>
        ),
        children: [
          // Home / Principal
          {
            path: ROUTES.DASHBOARD.PRINCIPAL,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <HomePage />
              </Suspense>
            ),
          },

          // Employees
          {
            path: ROUTES.EMPLOYEES.ROOT,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <EmployeeListPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.EMPLOYEES.CREATE,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <CreateEmployeePage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.EMPLOYEES.DETAIL(':id'),
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <EmployeeDetailPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.EMPLOYEES.SETTLEMENT(':id'),
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <SettlementCalculatorPage />
              </Suspense>
            ),
          },

          // Payroll
          {
            path: ROUTES.PAYROLL.ROOT,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <PayrollManagerPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.PAYROLL.CREATE,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <PayrollManagerPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.PAYROLL.DETAIL(':id'),
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <PayrollDetailPage />
              </Suspense>
            ),
          },

          // Profile
          {
            path: ROUTES.PROFILE.ROOT,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProfilePage />
              </Suspense>
            ),
          },

          // Settings
          {
            path: ROUTES.SETTINGS.ROOT,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <SystemSettingsPage />
              </Suspense>
            ),
          },

          // Reports
          {
            path: ROUTES.REPORTS.ROOT,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <LegalReportsPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={ROUTES.DASHBOARD.PRINCIPAL} replace />,
  },
]);
